/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import _ from "lodash";
import { useLocation } from "react-router-dom";
import {
  AlertComponent,
  BreadcrumbComponent,
  PaginationComponent,
  MUITable,
} from "../../../common/components";
import { useSetLoading } from "../../../common/hooks/appearance";
import {
  breadcrumbLink,
  getHeaders,
  userMngmtFilterOptions,
} from "./user-management-list.constant";
import UserManagementListView from "./user-management-list-view/user-management-list-view";
import UserManagementListAdd from "./user-management-list-add/user-management-list-add";
import UserManagementListFilter from "./user-management-list-filter/user-management-list-filter";
import UserManagementStatus from "./user-management-status/user-management-status";
import "./user-management-list.scss";
import { getCurrentTenantId } from "../../../utils/utils";
import { deleteTenantUsers } from "../../../api/settings";
import { getTenantsList, getTenetRoles, resendCredentialEmail } from "../../../api/tenets";
import { AlertMessage } from "../../tenant/tenant-details/tenant-details-types";
import { useLogin } from "../../../common/hooks/useUser";
import DeleteConfirmationPopup from "./delete-confirmation-popup/delete-confirmation-popup";
import { getUserDetails } from "../../../api/lead";
import { TenetListParams } from "../../tenant/tenant-list/tenant-list-helper";
import DeleteIcon from "../../../assets/icons/delete-outline.svg";
import UserManagementOptions from "./user-management-list-filter/user-management-list-filter-options/user-management-list-filter-options";
import { useResourcePermissions } from "../../../common/hooks/resource-permissions";
import MODULES from "../../../constants/modules";
import { Box } from "@mui/material";
import icons from "../../../assets/icons/icons";
import CustomTooltip from "../../../common/components/custom-tooltip";
import { isValidPhoneNumber } from "../../../utils/utils";

interface SortOption {
  sortDirection: string | null;
  sortBy: string | null;
}

const UserManagementList: React.FC = () => {
  const [isViewEditOpen, setIsViewEditOpen] = useState<boolean>(false);
  const [viewOpen, setViewOpen] = useState<boolean>(false);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserDetails, setSelectedUserDetails] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);
  const [data, setData] = useState<any[]>([]);
  const [crumbMappingOption, setCrumbMappingOption] =
    useState<any>(breadcrumbLink);
  const [sortOption, setSortOption] = useState<SortOption>({
    sortDirection: null,
    sortBy: null,
  });
  const [alertMessage, setAlertMessage] = React.useState<AlertMessage>({
    open: false,
    message: "",
    type: undefined,
  });
  const [activeFilters, setActiveFilters] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [companyNames, setCompanyNames] = useState<any[]>([]);
  const [tenetRoles, setTenetRoles] = useState<any[]>([]);
  const [isDeletePopupOpen, setDeletePopupOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState<number>();
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [company, setCompany] = useState<any>();
  const [tenantId, setTenantId] = useState<number | null>(getCurrentTenantId());
  const [companyfilters, setCompanyFilters] = useState<any>({});

  const location = useLocation();
  const tenantDetails = useLogin();
  const isSuperParter = useMemo(() => {
    return tenantDetails?.tenantDetails?.tenantType === "SuperTenant";
  }, [tenantDetails?.tenantDetails?.tenantType]);

  const userRole = tenantDetails.userDetails.roles.role;

  const isuserLimitExceeded = useMemo(() => {
    return tenantDetails?.userLimitExceeded;
  }, [tenantDetails?.tenantDetails?.tenantType]);

  const tenatName = tenantDetails?.tenantDetails?.companyName;
  const headers = getHeaders();

  const { canDelete } = useResourcePermissions(MODULES.USER_LISTS);

  useEffect(() => {
    if (location.state?.message) {
      setAlertMessage({
        open: true,
        message: location.state.message,
        type: location.state.type,
      });
    }
    window.history.replaceState({}, document.title);
  }, [location.state]);

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      setSearchText(text);
      setCurrentPage(1);
    }, 500),
    []
  );

  const handleCancel = async () => {
    setIsViewEditOpen(false);
    setCrumbMappingOption(breadcrumbLink);
  };

  const handleSort = (column: string, sortType: string) => {
    let sortValues: SortOption = { sortBy: null, sortDirection: null };
    if (sortType !== "Unsort") {
      const mappedField = mapColumnNameToField(column);
      sortValues = { sortBy: mappedField, sortDirection: sortType };
    }
    setCurrentPage(1);
    setSortOption(sortValues);
  };

  const fetchUsers = async (page: number = 1, search: string = "") => {
    try {
      setIsLoading(true);
      const offset = (page - 1) * pageSize;
      let params: TenetListParams = {
        limit: pageSize,
        offset: offset,
      };
      if (sortOption?.sortBy) {
        params = { ...params, ...sortOption };
      }
      if (search) {
        params.searchText = search;
      }
      if (Object.keys(activeFilters).length) {
        params = { ...params, ...activeFilters };
      }
      if (Object.keys(companyfilters).length) {
        params = { ...params, ...companyfilters };
      }
      const response = await getUserDetails(
        { ...params, allUsers: true },
        "User List"
      );
      setData(response.data.data.data.data);
      setTotalRecords(response.data.data.data.totalRecords);
      setIsLoading(false);
    } catch (error: any) {
      console.error("Error fetching user profile:", error);
      return null;
    } finally {
      setIsLoading(false);
      setCrumbMappingOption(breadcrumbLink);
    }
  };

  const getTenentList = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getTenantsList({ isActive: true, dropDown: true });
      const items = response?.data?.data || [];
      const uniqueCompanies: { id: number; company_name: string }[] = [];

      for (const item of items) {
        if (item?.company_name) {
          uniqueCompanies.push({
            id: item.id,
            company_name: item.company_name,
          });
        }
      }
      setCompanyNames(uniqueCompanies);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const tenantCurrentId = activeFilters ? tenantId : getCurrentTenantId();

  const getRole = useCallback(async () => {
    try {
      setIsLoading(true);
      const response: any = await getTenetRoles({ tenantId: tenantCurrentId });
      if (response?.data?.success) {
        const roleOptions = response?.data?.data?.map((item: any) => ({
          value: item?.id,
          label: item?.role,
        }));

        setTenetRoles(roleOptions);
      }
    } catch (error: any) {
      setAlertMessage({
        open: true,
        message: error?.response?.data?.message || "Something went wrong!",
        type: "error",
      });
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [company]);

  useEffect(() => {
    getRole();
    if (isSuperParter) {
      getTenentList();
    }
  }, [getTenentList, getRole]);

  useEffect(() => {
    if (!viewOpen) {
      fetchUsers(currentPage, searchText ?? "");
    }
  }, [
    currentPage,
    searchText,
    sortOption,
    viewOpen,
    activeFilters,
    tenantId,
    setData,
    companyfilters,
  ]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handleDeleteUser = async () => {
    try {
      setIsLoading(true);
      const paylod = {
        users: [{ id: deleteUserId, isDelete: true }],
      };
      const updatedUsers = await deleteTenantUsers(tenantId || 0, paylod);
      setDeletePopupOpen(false);
      fetchUsers();
      setAlertMessage({
        open: true,
        message: "User deleted successfully",
        type: "success",
      });
    } catch (error) {
      setAlertMessage({
        open: true,
        message: "Failed to delete user",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const mapColumnNameToField = (columnName: string): string => {
    const mapping: Record<string, string> = {
      firstName: "first_name",
      lastName: "last_name",
      email: "email",
      phone: "phone_number",
      status: "status",
      dateAdded: "dateAdded",
      role: "role",
    };

    return mapping[columnName] || columnName;
  };

  const handleViewClick = async (userId: number, userName: any) => {
    setCrumbMappingOption([
      {
        label: "User Management",
        link: "/home/user-management/user-management-list",
        handleClick: (event: any) => {
          event.preventDefault();
          setCrumbMappingOption(breadcrumbLink);
          setViewOpen(false);
          setIsViewEditOpen(false);
          setCurrentPage(1);
        },
      },
      {
        label: "User List",
        link: "/home/user-management/user-management-list",
        handleClick: (event: any) => {
          event.preventDefault();
          setCrumbMappingOption(breadcrumbLink);
          setViewOpen(false);
          setIsViewEditOpen(false);
          setCurrentPage(1);
        },
      },
      { label: userName, link: "" },
    ]);

    setViewOpen(!viewOpen);
    setSelectedUserId(userId);
  };

  useEffect(() => {
    if (!selectedUserId || !data || data?.length === 0) return;
    const userDetails = data.find((user) => user.id === selectedUserId);
    const role = tenetRoles.find((r) => r.value === userDetails?.role_id);

    const updatedUserDetails = {
      ...userDetails,
      role: role,
    };
    setSelectedUserDetails(updatedUserDetails || null);
  }, [selectedUserId, data]);

  const handleAddClick = async (userId: number) => {
    setCrumbMappingOption([
      { label: "User Management", link: "#", handleClick: () => {} },
      {
        label: "User List",
        link: "/home/user-management/user-management-list",
        handleClick: (event: any) => {
          event.preventDefault();
          setCrumbMappingOption(breadcrumbLink);
          setViewOpen(false);
          setIsViewEditOpen(false);
          setCurrentPage(1);
        },
      },
      { label: "Add User", link: "" },
    ]);

    setIsViewEditOpen(!isViewEditOpen);
    setSelectedUserId(userId);
  };

  const formattedData = useMemo(() => {
    return data.map((item: any) => {
      const {
        id,
        first_name,
        last_name,
        email,
        phone_number,
        role_id,
        created_at,
        is_primary_user,
        companyName,
        tenantId,
        shouldResendUserCredentials
      } = item;
      const role = tenetRoles.find((r) => r.value === role_id);

      const firstName = first_name?.trim() || "-";
      const lastName = last_name?.trim() || "-";
      const roleName = role?.label || "-";
      const dateAddedFormatted = created_at
        ? new Date(created_at).toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
        : "-";
      const status = item.status
        ? item.status.charAt(0).toUpperCase() +
          item.status.slice(1).toLowerCase()
        : "";

      const companyData = tenantCurrentId === 1 ? "AFP" : companyName;

      return {
        id,
        firstName,
        lastName,
        fullName: `${firstName} ${lastName}`.trim(),
        email: email || "-",
        phone: isValidPhoneNumber(phone_number) ? phone_number : "-",
        role: roleName,
        dateAdded: dateAddedFormatted,
        status: <UserManagementStatus status={status} />,
        is_primary_user,
        company: companyData,
        shouldResendUserCredentials
      };
    });
  }, [data]);

  useEffect(() => {
    if (!data?.length) return;
    const item = data[0];
    setCompany(item.companyName ?? tenatName);
    setTenantId(item.tenantId ?? getCurrentTenantId());
  }, [data]);

  const noResultsTxt = useMemo(() => {
    if (isLoading) {
      return "Loading users...";
    }
    if (searchText) {
      return "No users found.";
    }
    return "No users found";
  }, [isLoading, searchText]);

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const dateFilterActive =
      activeFilters.onBoardedAtEnd || activeFilters.onBoardedAtStart;

    const otherFilters = { ...activeFilters };
    delete otherFilters.onBoardedAtEnd;
    delete otherFilters.onBoardedAtStart;

    const otherActiveFilters = Object.values(otherFilters).filter(
      (val) =>
        val !== undefined &&
        val !== "" &&
        val !== null &&
        !(Array.isArray(val) && val.length === 0)
    ).length;

    if (dateFilterActive) {
      count += 1;
    }

    count += otherActiveFilters;

    return count;
  }, [activeFilters]);

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

    const handleResendCredentialEmail= async (user: any) =>{
      setIsLoading(true);
      try {
       await resendCredentialEmail(tenantCurrentId ?? 0, user?.id);
        setAlertMessage({
          open: true,
          message: "User credential email resent successfully.",
          type: "success",
        });
      } catch(error: any) {
        console.error("Error resending credential email:", error);
        setAlertMessage({
          open: true,
          message: "Error resending user credential email.",
          type: "error",
        });
      } finally {
        setIsLoading(false);
      }
    }

  return (
    <>
      <div className="user-management-wrapper">
        <div className="breadcrumb-wrapper">
          <BreadcrumbComponent breadcrumbLink={crumbMappingOption} />
        </div>
        <div className="user-management-list-container">
          {!viewOpen && !isViewEditOpen ? (
            <>
              <>
                <UserManagementListFilter
                  isOpenFilter={isOpenFilter}
                  setIsOpenFilter={setIsOpenFilter}
                  onSearch={handleSearch}
                  handleAddClick={handleAddClick}
                  selectedUserManagementListFilter={appliedFilterCount}
                  companyNames={companyNames}
                  onApplyFilter={(filterData: any) => {
                    setCompanyFilters(filterData);
                    // setActiveFilters(filterData);
                  }}
                  filters={companyfilters}
                />
              </>
              {isuserLimitExceeded && (
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Box className="alert-users-limit">
                    <img src={icons.TermOfServicesInfo} alt="img" />
                    <p>
                      Your CRM plan's user limit has been reached. Upgrade or
                      remove users to proceed.
                    </p>
                  </Box>
                </Box>
              )}
              <div className="user-management-list-table-filter">
                <div className="user-management-list-table">
                  <MUITable
                    headers={headers}
                    data={formattedData.map((item: any) => ({
                      key: item.id,
                      select: (
                        <>
                          {["Super Admin", "Admin"].includes(userRole) ? (
                            <>
                              <CustomTooltip title="Archive User" placement="bottom" arrow={false}>
                                <img
                                  src={DeleteIcon}
                                  className="fa fa-trash"
                                  style={{ cursor: "pointer" }}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    if (canDelete && !item.is_primary_user) {
                                      setDeleteUserId(item.id);
                                      setDeletePopupOpen(true);
                                    }
                                  }}
                                />
                              </CustomTooltip>
                              <span
                                className="email-resend-icon"
                                style={{ width: "24px"}}
                              >
                                {item.shouldResendUserCredentials ? (
                                  <CustomTooltip title="Resend User Credentials" placement="top" arrow={false}>
                                    <img
                                      src={icons.EmailResendIcons}
                                      alt="Resend"
                                      width={20}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleResendCredentialEmail(item);
                                      }}
                                      />
                                    </CustomTooltip>
                                ) : (
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: 20,
                                      height: 20,
                                    }}
                                  />
                                )}
                              </span>
                            </>
                          ) : (
                              <>
                              <CustomTooltip title="Archive User" placement="bottom" arrow={false}>
                                <img
                                  src={DeleteIcon}
                                  className="fa fa-trash"
                                  style={{ cursor: "pointer" }}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    if (canDelete && !item.is_primary_user) {
                                      setDeleteUserId(item.id);
                                      setDeletePopupOpen(true);
                                    }
                                    }}
                                  />
                              </CustomTooltip>
                            </>
                          )}
                        </>
                      ),
                      company: item.company,
                      firstName: item.firstName,
                      lastName: item.lastName,
                      email: item.email,
                      phone: item.phone,
                      role: item.role,
                      dateAdded: item.dateAdded,
                      status: item.status,
                    }))}
                    onSort={handleSort}
                    noResultsTxt={noResultsTxt}
                    onRowClick={(rowData: any) =>
                      handleViewClick(
                        rowData.key,
                        `${rowData.firstName} ${rowData.lastName}`
                      )
                    }
                    isShowUserCredentialEmailOption={[
                      "Super Admin",
                      "Admin",
                    ].includes(userRole)}
                  />
                </div>
                {isOpenFilter ? (
                  <UserManagementOptions
                    open={isOpenFilter}
                    filters={activeFilters}
                    onClose={() => setIsOpenFilter(false)}
                    companyNames={companyNames}
                    roles={tenetRoles}
                    onApplyFilters={(filterData, shouldClose = true) => {
                      try {
                        setActiveFilters(filterData);
                        if (shouldClose) setIsOpenFilter(false);
                        setCurrentPage(1);
                      } catch (error: any) {
                        console.error("Failed to apply filters:", error);
                        setAlertMessage({
                          open: true,
                          message: "Failed to apply filters. Please try again.",
                          type: "error",
                        });
                      }
                    }}
                  />
                ) : (
                  ""
                )}
              </div>
              {totalRecords && totalRecords > 0 ? (
                <div className="user-management-pagination-container">
                  <PaginationComponent
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                  />
                </div>
              ) : (
                <></>
              )}
            </>
          ) : isViewEditOpen ? (
            <UserManagementListAdd
              companyNames={companyNames}
              roles={tenetRoles}
              setViewOpen={setIsViewEditOpen}
              onSuccess={fetchUsers}
              onCancel={handleCancel}
            />
          ) : (
            <UserManagementListView
              setLoading={setIsLoading}
              selectedUserId={selectedUserId}
              setAlertMessage={setAlertMessage}
              setIsViewEditOpen={setIsViewEditOpen}
              setViewOpen={setViewOpen}
              selectedUserDetails={selectedUserDetails}
              companyNames={companyNames}
              roles={tenetRoles}
            />
          )}
          {alertMessage.open && (
            <AlertComponent
              message={alertMessage.message}
              type={alertMessage.type || "info"}
              open={alertMessage.open}
              onClose={() => setAlertMessage({ open: false })}
            />
          )}
          {deleteUserId && (
            <DeleteConfirmationPopup
              isOpen={isDeletePopupOpen}
              onClose={() => setDeletePopupOpen(!isDeletePopupOpen)}
              handleDelete={handleDeleteUser}
              deleteId={deleteUserId}
              label="User"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default UserManagementList;
